/**
 * OfflineIndicator Component
 * Shows the user's online/offline status and provides offline functionality info
 * Displays sync status and offline actions queue
 */

import { useState, useEffect } from 'react';
import { useOnlineStatus } from '../../hooks/usePWA';
import Button from '../ui/Button';

interface OfflineIndicatorProps {
  showDetails?: boolean;
  position?: 'top' | 'bottom';
  className?: string;
}

export default function OfflineIndicator({
  showDetails = false,
  position = 'top',
  className = '',
}: OfflineIndicatorProps) {
  const isOnline = useOnlineStatus();
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [pendingActions, setPendingActions] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

// Replace the useEffect on lines 28-46 with this:

// Show offline message only when transitioning states, not on initial load
useEffect(() => {
  const isInitialMount = showOfflineMessage === false && lastSyncTime === null;
  
  if (!isInitialMount) {
    if (!isOnline) {
      setShowOfflineMessage(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowOfflineMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      // Update last sync time when coming back online
      setLastSyncTime(new Date());
      // Show brief online message only when coming back from offline
      setShowOfflineMessage(true);
      const timer = setTimeout(() => {
        setShowOfflineMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }
}, [isOnline, showOfflineMessage, lastSyncTime]);

  // Listen for service worker messages about sync status
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data;
      
      if (type === 'SYNC_SUCCESS') {
        setPendingActions(prev => Math.max(0, prev - 1));
      } else if (type === 'OFFLINE_ACTION_STORED') {
        setPendingActions(prev => prev + 1);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowOfflineMessage(false);
  };

  // Persistent status indicator (always visible)
  const StatusIndicator = () => (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
      <span className={`text-xs font-medium ${isOnline ? 'text-green-700' : 'text-red-700'}`}>
        {isOnline ? 'Online' : 'Offline'}
      </span>
      {!isOnline && pendingActions > 0 && (
        <span className="text-xs text-gray-500">
          ({pendingActions} pending)
        </span>
      )}
    </div>
  );

  // Detailed status message (shows temporarily)
  const StatusMessage = () => {
    if (!showOfflineMessage) return null;

    return (
      <div className={`fixed left-4 right-4 z-50 ${position === 'top' ? 'top-4' : 'bottom-4'} md:left-auto md:right-4 md:max-w-md`}>
        <div className={`rounded-lg shadow-lg border p-4 ${
          isOnline 
            ? 'bg-green-50 border-green-200' 
            : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-start space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isOnline ? 'bg-green-100' : 'bg-orange-100'
            }`}>
              {isOnline ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
                </svg>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-semibold ${
                isOnline ? 'text-green-800' : 'text-orange-800'
              }`}>
                {isOnline ? 'Back Online!' : 'You\'re Offline'}
              </h4>
              
              <p className={`text-xs mt-1 ${
                isOnline ? 'text-green-700' : 'text-orange-700'
              }`}>
                {isOnline ? (
                  <>
                    Your connection has been restored.
                    {pendingActions > 0 && ` Syncing ${pendingActions} pending actions...`}
                  </>
                ) : (
                  'Some features may be limited. Your actions will be synced when you\'re back online.'
                )}
              </p>

              {showDetails && (
                <div className="mt-2 space-y-1">
                  {!isOnline && (
                    <div className="text-xs text-orange-600">
                      • You can still browse cached content
                      • Your actions will be saved and synced later
                      • Push notifications may be delayed
                    </div>
                  )}
                  
                  {isOnline && lastSyncTime && (
                    <div className="text-xs text-green-600">
                      Last synced: {lastSyncTime.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex space-x-2 mt-3">
                {!isOnline && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetry}
                    className="text-xs px-3 py-1"
                  >
                    Retry
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="text-xs px-3 py-1"
                >
                  Dismiss
                </Button>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <StatusIndicator />
      <StatusMessage />
    </>
  );
}